/**
 * Newsletter Popup - Team Line Custom Theme
 * 
 * Sport/performance brand popup behavior:
 * - Session-based display (once per session)
 * - Focus trap for accessibility
 * - ESC key and click-outside-to-close
 * - Shopify Email Marketing integration
 * - Reduced motion support
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKey: 'teamline_np_closed',
    showDelay: 3000,
    autoCloseDelay: 2500,
    scrollLockClass: 'np-open',
    debugMode: false // Set to true for testing (bypasses session check)
  };

  // State
  let popup = null;
  let overlay = null;
  let modal = null;
  let closeBtn = null;
  let form = null;
  let input = null;
  let submitBtn = null;
  let submitText = null;
  let submitLoader = null;
  let errorContainer = null;
  let errorText = null;
  let formState = null;
  let successState = null;
  
  let previousFocus = null;
  let focusableElements = [];
  let autoCloseTimer = null;

  /**
   * Initialize popup
   */
  function init() {
    popup = document.querySelector('[data-popup-id]');
    
    if (!popup) return; // Not rendered (disabled, not homepage, or user logged in)

    // Check if already closed this session
    if (!CONFIG.debugMode && hasClosedThisSession()) return;

    // Cache DOM references
    cacheElements();
    
    if (!overlay || !modal || !closeBtn) {
      console.warn('Newsletter popup: Required elements missing');
      return;
    }

    // Get show delay from data attribute
    const delay = parseInt(popup.dataset.showDelay, 10) || CONFIG.showDelay;

    // Bind events
    bindEvents();

    // Show after delay
    setTimeout(show, delay);
  }

  /**
   * Cache DOM element references
   */
  function cacheElements() {
    overlay = popup.querySelector('[data-popup-overlay]');
    modal = popup.querySelector('[data-popup-modal]');
    closeBtn = popup.querySelector('[data-popup-close]');
    form = popup.querySelector('form');
    input = popup.querySelector('[data-popup-input]');
    submitBtn = popup.querySelector('[data-popup-submit]');
    submitText = popup.querySelector('[data-popup-submit-text]');
    submitLoader = popup.querySelector('[data-popup-submit-loader]');
    errorContainer = popup.querySelector('[data-popup-error]');
    errorText = errorContainer?.querySelector('.np__error-text');
    formState = popup.querySelector('[data-popup-form-state]');
    successState = popup.querySelector('[data-popup-success-state]');
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    closeBtn.addEventListener('click', handleClose);
    overlay.addEventListener('click', handleClose);
    document.addEventListener('keydown', handleKeyDown);
    
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    // Prevent close when clicking inside modal
    modal.addEventListener('click', e => e.stopPropagation());
  }

  /**
   * Show the popup
   */
  function show() {
    if (!popup) return;

    // Store current focus
    previousFocus = document.activeElement;

    // Calculate scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty('--np-scrollbar-width', scrollbarWidth + 'px');
    }

    // Lock scroll
    document.body.classList.add(CONFIG.scrollLockClass);

    // Show
    popup.setAttribute('aria-hidden', 'false');

    // Mark as seen for this session so it only appears once
    markClosedForSession();

    // Update focusable elements
    updateFocusableElements();

    // Focus input after animation
    if (input) {
      setTimeout(() => input.focus(), 100);
    }

    // Announce to screen readers
    announce('Newsletter signup dialog opened');
  }

  /**
   * Close the popup
   */
  function close() {
    if (!popup) return;

    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(CONFIG.scrollLockClass);

    // Restore focus
    if (previousFocus) {
      previousFocus.focus();
    }

    // Mark closed for session
    markClosedForSession();

    // Clear auto-close timer
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }

    // Hide Precision H capture icon and other bottom corner widgets
    hideCaptureWidgets();

    announce('Newsletter signup dialog closed');
  }

  /**
   * Hide third-party capture widgets (hCaptcha, Precision H, etc.)
   * Note: If the widget doesn't hide, inspect the element and add its selector below
   */
  function hideCaptureWidgets() {
    // Common widget selectors to try hiding
    const widgetSelectors = [
      // hCaptcha specific
      '.h-captcha',
      '[class*="hcaptcha"]',
      '[class*="h-captcha"]',
      '[id*="hcaptcha"]',
      '[id*="h-captcha"]',
      'iframe[src*="hcaptcha"]',
      'iframe[src*="h-captcha"]',
      '.h-captcha-badge',
      '[class*="hcaptcha-badge"]',
      // Precision Hydration specific
      '[class*="precision"]',
      '[id*="precision"]',
      '[class*="ph-capture"]',
      '[id*="ph-capture"]',
      // Common widget containers
      '[class*="widget-container"]',
      '[class*="capture-icon"]',
      '[class*="floating-icon"]',
      // Generic bottom corner widgets
      '[style*="position: fixed"][style*="bottom"]',
      '.widget-bubble',
      '#widget-bubble',
      // Email capture tools
      '[class*="email-capture"]',
      '[id*="email-capture"]'
    ];

    widgetSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Aggressively hide with multiple style properties
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.style.opacity = '0';
          el.style.pointerEvents = 'none';
          el.style.position = 'absolute';
          el.style.width = '0';
          el.style.height = '0';
          el.style.overflow = 'hidden';
          
          // Also set attribute for CSS targeting
          el.setAttribute('aria-hidden', 'true');
        });
      } catch (e) {
        // Invalid selector, skip
      }
    });

    // Also hide any hCaptcha iframes that might load later
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if it's hCaptcha related
            const className = node.className || '';
            const id = node.id || '';
            if (
              className.includes('hcaptcha') ||
              className.includes('h-captcha') ||
              id.includes('hcaptcha') ||
              id.includes('h-captcha') ||
              (node.tagName === 'IFRAME' && node.src && node.src.includes('hcaptcha'))
            ) {
              node.style.display = 'none';
              node.style.visibility = 'hidden';
              node.setAttribute('aria-hidden', 'true');
            }
          }
        });
      });
    });

    // Observe document body for new hCaptcha elements
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handle close button click
   */
  function handleClose(e) {
    e.preventDefault();
    close();
  }

  /**
   * Handle keyboard events
   */
  function handleKeyDown(e) {
    if (!popup || popup.getAttribute('aria-hidden') === 'true') return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === 'Tab') {
      handleTabTrap(e);
    }
  }

  /**
   * Handle Tab key for focus trapping
   */
  function handleTabTrap(e) {
    if (focusableElements.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Update focusable elements list
   */
  function updateFocusableElements() {
    if (!modal) return;

    const selectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ];

    focusableElements = Array.from(
      modal.querySelectorAll(selectors.join(','))
    ).filter(el => el.offsetParent !== null && !el.hasAttribute('hidden'));
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e) {
    e.preventDefault();

    if (!form || !input || !submitBtn) return;

    const email = input.value.trim();

    // Client-side validation
    if (!isValidEmail(email)) {
      showError('Enter a valid email');
      input.focus();
      return;
    }

    // Hide previous error
    hideError();

    // Loading state
    setLoading(true);

    try {
      const result = await submitToShopify(form);

      if (result.success) {
        showSuccess();
      } else {
        showError(result.message || 'Something went wrong. Try again.');
      }
    } catch (err) {
      console.error('Newsletter popup error:', err);
      showError('Unable to subscribe. Try again later.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Submit form to Shopify
   */
  async function submitToShopify(formEl) {
    const formData = new FormData(formEl);
    const action = formEl.getAttribute('action') || window.location.href;

    try {
      const response = await fetch(action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        }
      });

      if (response.redirected || response.ok) {
        const text = await response.text();
        
        // Check for error indicators
        if (text.includes('form_errors') || 
            (text.includes('error') && text.includes('email')) ||
            text.includes('Invalid')) {
          return {
            success: false,
            message: 'Already subscribed or invalid email.'
          };
        }

        return { success: true };
      }

      return { 
        success: false, 
        message: 'Subscription failed. Try again.' 
      };
    } catch (err) {
      return submitFallback(formEl);
    }
  }

  /**
   * Fallback form submission via iframe
   */
  function submitFallback(formEl) {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.name = 'np-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const originalTarget = formEl.target;
      formEl.target = iframe.name;

      let handled = false;
      
      iframe.addEventListener('load', function() {
        if (handled) return;
        handled = true;

        formEl.target = originalTarget;
        setTimeout(() => document.body.removeChild(iframe), 1000);
        resolve({ success: true });
      });

      setTimeout(() => {
        if (handled) return;
        handled = true;
        
        formEl.target = originalTarget;
        document.body.removeChild(iframe);
        resolve({ success: true });
      }, 5000);

      formEl.submit();
    });
  }

  /**
   * Validate email format
   */
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Set loading state
   */
  function setLoading(loading) {
    if (!submitBtn) return;

    if (loading) {
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
    } else {
      submitBtn.classList.remove('is-loading');
      submitBtn.disabled = false;
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    if (!errorContainer || !errorText) return;

    errorText.textContent = message;
    errorContainer.classList.add('is-visible');
    announce('Error: ' + message);
  }

  /**
   * Hide error message
   */
  function hideError() {
    if (!errorContainer) return;
    errorContainer.classList.remove('is-visible');
  }

  /**
   * Show success state
   */
  function showSuccess() {
    if (!formState || !successState) return;

    formState.classList.add('is-hidden');

    setTimeout(() => {
      successState.removeAttribute('aria-hidden');
      void successState.offsetWidth; // Force reflow
      successState.classList.add('is-visible');
      announce('Success! You have been subscribed.');
    }, 150);

    markClosedForSession();

    // Auto-close
    autoCloseTimer = setTimeout(close, CONFIG.autoCloseDelay);
  }

  /**
   * Check if popup was closed this session
   */
  function hasClosedThisSession() {
    try {
      return sessionStorage.getItem(CONFIG.storageKey) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark popup as closed for session
   */
  function markClosedForSession() {
    try {
      sessionStorage.setItem(CONFIG.storageKey, 'true');
    } catch (e) {
      // sessionStorage not available
    }
  }

  /**
   * Announce message to screen readers
   */
  function announce(message) {
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.className = 'np__sr-only';
    el.textContent = message;

    document.body.appendChild(el);
    setTimeout(() => document.body.removeChild(el), 1000);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
